/**
 * /owner/membership/plans — list + create + edit + soft-delete.
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, EmptyState, FullSpinner, QueryError, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { Input, FieldRow, Select } from '@/components/ui_primitives/Field';
import TierBadge from '@/components/membership/TierBadge';
import { rupiah } from '@/lib/membershipFmt';

export default function OwnerMembershipPlansPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();
  const [editingPlan, setEditingPlan] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const plansQ = useQuery({
    queryKey: qk.ownerMembershipPlans(brandId),
    queryFn: () => api.ownerMembershipPlans(token, brandId),
    enabled: !!token && !!brandId,
  });

  const tiersQ = useQuery({
    queryKey: qk.ownerMembershipTiers(brandId),
    queryFn: () => api.ownerMembershipTiers(token, brandId),
    enabled: !!token && !!brandId,
  });

  const delM = useMutation({
    mutationFn: (id) => api.ownerMembershipPlanDelete(token, id),
    onSuccess: () => {
      toast.success('Plan archived');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipPlans(brandId) });
    },
    onError: (e) => toast.error(e?.detail || 'Could not delete plan'),
  });

  if (plansQ.isLoading || tiersQ.isLoading) return <FullSpinner />;
  if (plansQ.isError) return <QueryError error={plansQ.error} onRetry={() => plansQ.refetch()} />;

  const rawTiers = tiersQ.data ?? [];
  const tierMap = Object.fromEntries(rawTiers.map((t) => [t.id, t]));
  const plans = (plansQ.data ?? []).map((p) => ({
    ...p,
    tier: p.tier || tierMap[p.tier_id] || null,
  }));
  const activeTiers = rawTiers.filter((t) => t.active);

  return (
    <div data-testid="owner-membership-plans-page">
      <PageHeader
        kicker="Memberships"
        title="Plans"
        subtitle="Plans link a tier to a price and duration. Soft-deletes preserve historical assignments."
        actions={
          <Button
            variant="primary" size="md" leftIcon={<Plus size={14} />}
            onClick={() => { setEditingPlan(null); setModalOpen(true); }}
            testID="plan-create-button"
            disabled={activeTiers.length === 0}
          >
            New Plan
          </Button>
        }
      />

      {activeTiers.length === 0 ? (
        <div className="mb-6 rounded-2xl border border-amber-500/25 bg-amber-500/5 px-4 py-3 text-sm text-amber-200" data-testid="plan-no-tier-banner">
          You need at least one active tier before creating a plan. Head to <span className="font-semibold">Tiers</span> first.
        </div>
      ) : null}

      <SectionCard padded={false} testID="plan-section-card">
        {plans.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title="No plans yet"
              subtitle="Plans are the SKUs your cashiers and owners sell to customers."
              testID="plan-empty"
            />
          </div>
        ) : (
          <Table testID="plans-table">
            <THead>
              <Tr>
                <Th>Name</Th>
                <Th>Tier</Th>
                <Th align="right">Price</Th>
                <Th align="center">Duration</Th>
                <Th align="center">Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {plans.map((p) => (
                <Tr key={p.id} testID={`plan-row-${p.id}`}>
                  <Td>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-[11px] text-white/40 mt-0.5 font-mono">
                      Type: Purchased
                    </div>
                  </Td>
                  <Td>{p.tier ? <TierBadge tier={p.tier} /> : <span className="text-white/40">—</span>}</Td>
                  <Td align="right" className="font-mono text-gold">{rupiah(p.price)}</Td>
                  <Td align="center">
                    {p.is_lifetime ? (
                      <StatusPill variant="gold">Lifetime</StatusPill>
                    ) : (
                      <span className="text-white/70">{p.duration_days}d</span>
                    )}
                  </Td>
                  <Td align="center">
                    <StatusPill variant={p.active ? 'green' : 'slate'}>
                      {p.active ? 'Active' : 'Archived'}
                    </StatusPill>
                  </Td>
                  <Td align="right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        variant="ghost" size="sm" leftIcon={<Edit2 size={12} />}
                        onClick={() => { setEditingPlan(p); setModalOpen(true); }}
                        testID={`plan-edit-${p.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger" size="sm" leftIcon={<Trash2 size={12} />}
                        loading={delM.isPending && delM.variables === p.id}
                        onClick={() => delM.mutate(p.id)}
                        testID={`plan-delete-${p.id}`}
                      >
                        Archive
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </SectionCard>

      <div className="mt-4 text-xs text-white/45 inline-flex items-center gap-2"
        data-testid="plan-phase2-note">
        <Info size={12} className="text-gold/70" />
        Loyalty-Tier plans coming in Phase 2 — Phase 1 supports purchased plans only.
      </div>

      <PlanFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        brandId={brandId}
        tiers={activeTiers}
        editing={editingPlan}
      />
    </div>
  );
}

function PlanFormModal({ open, onClose, brandId, tiers, editing }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const isEdit = !!editing;
  const initial = useMemo(() => ({
    name: editing?.name ?? '',
    tier_id: editing?.tier_id ?? (tiers[0]?.id ?? ''),
    price: editing?.price ?? 0,
    duration_days: editing?.duration_days ?? 30,
    is_lifetime: !!editing?.is_lifetime,
  }), [editing, tiers]);

  const [name, setName] = useState(initial.name);
  const [tierId, setTierId] = useState(initial.tier_id);
  const [price, setPrice] = useState(initial.price);
  const [durationDays, setDurationDays] = useState(initial.duration_days);
  const [isLifetime, setIsLifetime] = useState(initial.is_lifetime);

  const m = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        tier_id: tierId,
        price: Number(price) || 0,
        is_lifetime: !!isLifetime,
        duration_days: isLifetime ? null : (Number(durationDays) || 30),
      };
      return isEdit
        ? api.ownerMembershipPlanPatch(token, editing.id, body)
        : api.ownerMembershipPlanCreate(token, brandId, body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Plan updated' : 'Plan created');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipPlans(brandId) });
      onClose();
    },
    onError: (e) => toast.error(e?.detail || 'Could not save plan'),
  });

  return (
    <Modal
      open={open} onClose={onClose}
      title={isEdit ? 'Edit Plan' : 'Create Plan'}
      subtitle="Phase 1: only purchased plans. Loyalty-tier plans are coming in Phase 2."
      size="md"
      testID="plan-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={m.isPending}
            testID="plan-modal-cancel">Cancel</Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={!name.trim() || !tierId}
            onClick={() => m.mutate()}
            testID="plan-modal-save"
          >
            {isEdit ? 'Save changes' : 'Create plan'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-xl border border-gold/25 bg-gold/5 px-3.5 py-2.5 text-xs text-gold inline-flex items-center gap-2"
          data-testid="plan-modal-type-hint">
          <Info size={12} /> Type: Purchased  ·  Loyalty-Tier plans coming in Phase 2
        </div>
        <FieldRow label="Name" required>
          <Input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. VIP Monthly"
            data-testid="plan-modal-name"
          />
        </FieldRow>
        <FieldRow label="Tier" required>
          <Select
            value={tierId} onChange={(e) => setTierId(e.target.value)}
            data-testid="plan-modal-tier"
          >
            {tiers.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </Select>
        </FieldRow>
        <FieldRow label="Price (Rp)" required>
          <Input
            type="number" min={0} value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="plan-modal-price"
          />
        </FieldRow>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-white/80 cursor-pointer">
            <input
              type="checkbox" checked={isLifetime}
              onChange={(e) => setIsLifetime(e.target.checked)}
              className="w-4 h-4 accent-gold"
              data-testid="plan-modal-lifetime"
            />
            Lifetime plan
          </label>
        </div>
        <FieldRow label="Duration (days)" hint={isLifetime ? 'Disabled for lifetime plans' : 'e.g. 30, 90, 365'}>
          <Input
            type="number" min={1} value={isLifetime ? '' : durationDays}
            disabled={isLifetime}
            onChange={(e) => setDurationDays(e.target.value)}
            data-testid="plan-modal-duration"
          />
        </FieldRow>
      </div>
    </Modal>
  );
}
